import { useCallback, useEffect, useState } from 'react'

import { API } from '@/api'
import { PrivilegeType } from '@/consts/team'

interface UseTeamBalanceResult {
  teamBalance: number // in cents
  isLoading: boolean
  error: string | null
  refetch: () => Promise<void>
}

/**
 * Hook to fetch team account balance when in platform context.
 * Uses privilege_type: 57 to get a team token, then fetches /user/account.
 */
export function useTeamBalance(
  teamId: number | null,
  enabled: boolean = true,
): UseTeamBalanceResult {
  const [teamBalance, setTeamBalance] = useState<number>(0)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)

  const getTeamToken = useCallback(async (): Promise<string | null> => {
    if (!teamId) return null

    try {
      const res = await API.getTeamToken({
        privilege_type: PrivilegeType.TeamToken,
        source_id: teamId,
      })
      if (res.token) {
        return `Bearer ${res.token}`
      }
      return null
    } catch (err) {
      console.error('Failed to get team token:', err)
      return null
    }
  }, [teamId])

  const fetchTeamBalance = useCallback(async () => {
    if (!enabled || !teamId) return

    setIsLoading(true)
    setError(null)

    try {
      // Step 1: Get team token
      const teamToken = await getTeamToken()
      if (!teamToken) {
        setError('Failed to get team token')
        return
      }

      // Step 2: Fetch account with team token
      const res = await API.getAccount(undefined, {
        headers: {
          Authorization: teamToken,
        },
      })

      if (res.code === 0 && res.account) {
        setTeamBalance(res.account.totalBonus?.moneyCent ?? 0)
      } else {
        setError(res.msg || 'Failed to fetch team balance')
      }
    } catch (err) {
      console.error('Failed to fetch team balance:', err)
      setError('Failed to fetch team balance')
    } finally {
      setIsLoading(false)
    }
  }, [enabled, teamId, getTeamToken])

  useEffect(() => {
    if (enabled && teamId) {
      fetchTeamBalance()
    }
  }, [enabled, teamId, fetchTeamBalance])

  return {
    teamBalance,
    isLoading,
    error,
    refetch: fetchTeamBalance,
  }
}
